import "react-multi-toggle/style.css";

import { FormDecoration, FormInput, Modal } from "@general-backoffice/core";
import InvalidFeedback from "@general-backoffice/core/lib/components/InvalidFeedback";
import { type BotConfig, type PaymentMethod, UserConfigSchema } from "@investhinks/api-client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import MultiToggle from "react-multi-toggle";
import { useSelector } from "react-redux";
import { Collapse, FormGroup, Label } from "reactstrap";

import { isV5fxEarlyAccess } from "../../config/v5fxEarlyAccess";
import { isV6fxEarlyAccess } from "../../config/v6fxEarlyAccess";
import { useFormHandlers } from "../../hooks/strapi";
import type { RootState } from "../../redux/reducers/session/state";
import { getDefaultStoploss } from "../../utils/getStoploss";
import isVipOrZeroPriceTier from "../../utils/isVipOrZeroPriceTier";
import LeverageSelectionInput, { LeverageInfoModal } from "../exchangeAccounts/LeverageSelectionInput";
import leverageOptions from "../exchangeAccounts/utils/leverageOptions";
// import StrategySelect from "../strategies/StrategySelect";
import PaymentMethodSelect from "../paymentMethods/PaymentMethodSelect";
import RiskStrategySelectionInput, { ProOnlyBadge } from "../strategies/RiskStrategySelectionInput";
import { v6TransitionNotice } from "./v6Transition";

// FIXME
// Everything related to forceCustomSL is a solution to force a certain sl for all new bots of strategies
// with that property. This messes up a lot of the logic previously used for showing certain parts of the
// sl input interface and complicates the state handling logic. Remove when possible

const DEFAULT_CUSTOM_STOPLOSS = 0.85;

// const steps =[
//   [
//     [0, .5],
//     [2, 5],
//   ],
//   [
//     [.5, 1],
//     [5, 10],
//   ]
// ]
//
// const scaleIntegerToFloat = (input, rangeMappings) => {
//   for (const [inputRange, outputRange] of rangeMappings) {
//     const [inputStart, inputEnd] = inputRange;
//     const [outputStart, outputEnd] = outputRange;
//
//     if (input >= inputStart && input <= inputEnd) {
//       const inputFraction = (input - inputStart) / (inputEnd - inputStart);
//       const scaledOutput = outputStart + inputFraction * (outputEnd - outputStart);
//       return scaledOutput;
//     }
//   }
//
//   //if smaller than smallest range, return smallest range
//   if (input < rangeMappings[0][0][0]) {
//     return rangeMappings[0][1][0];
//   }
//   //if larger than largest range, return largest range
//   if (input > rangeMappings[rangeMappings.length - 1][0][1]) {
//     return rangeMappings[rangeMappings.length - 1][1][1];
//   }
//
//   // If the input does not fall within any of the specified input ranges, return NaN or throw an error.
//   return NaN;
// };

interface UpdateBotModalProps {
  bot: BotConfig | null;
  originalBot?: BotConfig | null;
  setBot: React.Dispatch<React.SetStateAction<BotConfig | null>>;
  close: () => void;
  update: (options?: { paymentMethodId?: number | string }) => void;
  isLoading: boolean;
  exchangeType?: string;
}

const UpdateBotModal = ({ bot, originalBot, setBot, close, update, isLoading, exchangeType }: UpdateBotModalProps) => {
  const { t } = useTranslation("bots", { keyPrefix: "createBotModal" });
  const { t: tRisks } = useTranslation("utils", { keyPrefix: "risks" });

  const { user } = useSelector((state: RootState) => state.sessionState);
  // V5FX is early-access only; gate by the effective (impersonation-aware) user's email.
  const v5fxEarlyAccess = isV5fxEarlyAccess(user?.email);
  const v6fxEarlyAccess = isV6fxEarlyAccess(user?.email);
  const { id, strategy, exchange_account, userConfig: rawUserConfig, stats /*, user*/ } = bot || {};
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const { defaultLeverage } = exchange_account || {};
  // Use exchangeType prop (passed from parent which has the full ExchangeAccount with exchange relation)
  const effectiveExchangeType = exchangeType;
  const isSupportedExchangeType = effectiveExchangeType === "tradfi";
  // Parse userConfig from generic Record to typed UserConfig
  const userConfig = useMemo(() => {
    if (!rawUserConfig) return undefined;
    const result = UserConfigSchema.safeParse(rawUserConfig);
    return result.success ? result.data : undefined;
  }, [rawUserConfig]);
  const { risk, customRisk, advancedStopLoss, maxActiveBalance, assets, maxLeverage } = userConfig || {};
  const { useCustomStopLoss, customStopLoss, stopLossEnabled } = advancedStopLoss || {};
  const currDD = stats?.drawdown;
  const transitionNotice = v6TransitionNotice(originalBot, bot);
  const transitionBlocked = transitionNotice === "positions" || transitionNotice === "pause";

  // Modals
  const [showInfoModal, setInfoModal] = useState<string | null>(null);
  const [[stoplossToAccept, stoplossWarningModal], setStoplossWarning] = useState<[string | boolean | null, boolean]>([
    null,
    false,
  ]);
  const [[disclaimerModal, disclaimerAccepted], setDisclaimer] = useState([false, false]);
  useEffect(() => {
    if (bot?.id) setDisclaimer([false, true]);
  }, [bot?.id]);

  const { handleInputChange, handleInputEvent } = useFormHandlers(setBot);
  // const handleInputMultiChange = useCallback(props => setBot(bot => ({...bot, ...props})), [setBot])

  // Store handleInputChange in ref to use in effects without causing loops
  const handleInputChangeRef = useRef(handleInputChange);
  useEffect(() => {
    handleInputChangeRef.current = handleInputChange;
  }, [handleInputChange]);

  // Initialization flags - reset when bot changes
  const initRefs = useRef({
    assets: false,
    customRisk: false,
    leverage: false,
    customStoploss: false,
    stoplossConfig: false,
  });
  useEffect(() => {
    initRefs.current = {
      assets: false,
      customRisk: false,
      leverage: false,
      customStoploss: false,
      stoplossConfig: false,
    };
  }, [bot?.id]);

  // For now all strategies will have the same assets / TODO: change this
  const strategyId = strategy?.id;
  useEffect(() => {
    if (!initRefs.current.assets && !assets && strategyId) {
      initRefs.current.assets = true;
      handleInputChangeRef.current("userConfig.assets", ["EURUSD", "GBPUSD"]);
    }
  }, [assets, strategyId]);

  // Set default custom risk when custom risk is selected
  const isCustomRisk = risk === "custom";
  useEffect(() => {
    if (!initRefs.current.customRisk && customRisk == null && isCustomRisk) {
      initRefs.current.customRisk = true;
      handleInputChangeRef.current("userConfig.customRisk", 0.5);
    }
  }, [customRisk, isCustomRisk]);

  // Set default leverage range based on the exchangeAccount's defaultLeverage
  const isNewBot = !bot;
  useEffect(() => {
    if (!initRefs.current.leverage && !maxLeverage && defaultLeverage && isNewBot) {
      initRefs.current.leverage = true;
      const leverageRange = leverageOptions.find((o) => o.value === defaultLeverage)?.leverageRange || [];
      const [minLev, maxLev] = leverageRange;
      handleInputChangeRef.current("userConfig.minLeverage", minLev);
      handleInputChangeRef.current("userConfig.maxLeverage", maxLev);
    }
  }, [maxLeverage, defaultLeverage, isNewBot]);

  // SL input states
  const forcedCustomSL = strategy?.configOptions?.forceCustomSL;
  const forcedCustomSlIsApplied =
    forcedCustomSL &&
    !!advancedStopLoss &&
    ((useCustomStopLoss && customStopLoss === forcedCustomSL) || !stopLossEnabled);

  // Handle stoploss
  const stoplossOn = stopLossEnabled !== false;
  const defaultRiskStoploss = useMemo(
    () =>
      strategy && risk
        ? getDefaultStoploss(
            { name: strategy.name ?? undefined, configOptions: strategy.configOptions ?? undefined },
            risk
          ) || 0.5
        : null,
    [strategy, risk]
  );
  const selectedStopLoss = stoplossOn ? (useCustomStopLoss ? "custom" : true) : false;
  const stoplossLabel = stoplossOn
    ? useCustomStopLoss
      ? ((customStopLoss ?? 0) * 100).toFixed(0)
      : (defaultRiskStoploss ?? 0) * 100
    : null;

  // Advisory: a stop loss under 50% is unusually low for the medium risk level
  // (medium's technical SL sits around 60%). Inform the user so they can review it.
  const effectiveStoploss = useCustomStopLoss ? (customStopLoss ?? null) : defaultRiskStoploss;
  const showMediumRiskLowStoploss =
    risk === "medium" && stoplossOn && effectiveStoploss != null && effectiveStoploss < 0.5;

  // Set default custom stoploss when custom stoploss is selected
  useEffect(() => {
    const riskBasedDefault = risk === "low" || risk === "ultra_low" ? 0.3 : 0.5;
    const defaultSlValue = forcedCustomSL || defaultRiskStoploss || riskBasedDefault;
    if (!initRefs.current.customStoploss && useCustomStopLoss && !customStopLoss && defaultSlValue) {
      initRefs.current.customStoploss = true;
      handleInputChangeRef.current("userConfig.advancedStopLoss.customStopLoss", defaultSlValue);
    }
  }, [useCustomStopLoss, customStopLoss, forcedCustomSL, defaultRiskStoploss, risk]);

  // Set default stoploss when custom risk is selected or when forced custom stoploss is set on a new bot
  const defaultCustomSL = forcedCustomSL || customStopLoss || DEFAULT_CUSTOM_STOPLOSS;
  useEffect(() => {
    const shouldSetStoplossConfig =
      (isCustomRisk && stoplossOn) || (forcedCustomSL && !id && (!advancedStopLoss || !!strategy));
    if (!initRefs.current.stoplossConfig && shouldSetStoplossConfig) {
      initRefs.current.stoplossConfig = true;
      handleInputChangeRef.current("userConfig.advancedStopLoss", {
        useCustomStopLoss: true,
        stopLossEnabled: true,
        customStopLoss: defaultCustomSL,
      });
    }
  }, [isCustomRisk, stoplossOn, forcedCustomSL, id, advancedStopLoss, strategy, defaultCustomSL]);

  const maxActiveBalanceOn = maxActiveBalance != null;
  const defaultMinCustomSL = 0.3;
  // Show defaultMinCustomSL as min unless currDD + 5 > defaultMinCustomSL
  const minSL = currDD ? Math.max(defaultMinCustomSL, Math.ceil(currDD + 5) / 100) : defaultMinCustomSL;
  return (
    <>
      <LeverageInfoModal isOpen={showInfoModal === "leverage"} close={() => setInfoModal(null)} />

      <RiskInfoModal isOpen={showInfoModal === "risk"} close={() => setInfoModal(null)} />

      <StoplossInfoModal isOpen={showInfoModal === "SL"} close={() => setInfoModal(null)} />

      <MABInfoModal isOpen={showInfoModal === "MAB"} close={() => setInfoModal(null)} />

      <Modal.FormContainer
        // title="Set up a new bot"
        size="md"
        title={t(bot?.id ? "titleUpdate" : "titleCreate")}
        isOpen={!!bot}
        toggleOpen={close}
        closeText={t("closeBtn")}
        buttonSave={{
          children: t(bot?.id ? "submitBtnUpdate" : "submitBtnCreate"),
          // Nothing is configurable for a non-tradfi account, so submitting
          // would post an unconfigured bot.
          disabled: isLoading || !isSupportedExchangeType || transitionBlocked,
        }}
        isLoading={isLoading}
        onSubmit={() => { if (!transitionBlocked) update({ paymentMethodId: paymentMethod?.id }); }}
      >
        {/*<StrategySelect*/}
        {/*  label="Escoge tu bot"*/}
        {/*  strategy={strategy}*/}
        {/*  setStrategy={strategy => handleInputChange('strategy', strategy)}*/}
        {/*  exchangeType={exchange_account?.exchange?.type}*/}
        {/*  className="mb--4"*/}
        {/*  disabled={isLoading}*/}
        {/*/>*/}

        {/*{!!strategy && <>*/}
        {/*<FormDecoration.LineTitle title="Configuración" />*/}

        {isSupportedExchangeType ? (
          <>
            <LeverageSelectionInput
              label={
                <>
                  {t("form.leverage")}
                  <span onClick={() => setInfoModal("leverage")} className="p-1 m--1 pointer-cursor">
                    <i className="fas fa-info-circle" />
                  </span>
                </>
              }
              onChange={(maxLeverage: number, minLeverage: number) =>
                handleInputChange("userConfig", { ...(userConfig || {}), minLeverage, maxLeverage })
              }
              leverage={userConfig?.maxLeverage}
              isLoading={isLoading}
              required
            />
            <FormDecoration.Line />

            <RiskStrategySelectionInput
              label={
                <>
                  {t("form.strategy")}
                  <span onClick={() => setInfoModal("risk")} className="p-1 m--1 pointer-cursor">
                    <i className="fas fa-info-circle" />
                  </span>
                </>
              }
              strategy={strategy}
              risk={risk}
              onChange={(strategy, risk) => {
                handleInputChange("strategy", strategy || null);
                handleInputChange("userConfig.risk", risk || null);
              }}
              exchangeType={effectiveExchangeType ?? undefined}
              isLoading={isLoading}
              v4Only={!id} // current-gen-only for new bots
              v5fxEarlyAccess={v5fxEarlyAccess}
              v6fxEarlyAccess={v6fxEarlyAccess}
              required
            />

            {transitionNotice && <div className="alert alert-warning mt-3" role="alert">
              {t(`v6Transition.${transitionNotice}`, {count: originalBot?.stats?.openTrades ?? 0})}
            </div>}

            <Collapse isOpen={risk === "custom"} className="pt-4 mb--4">
              <FormInput.Range
                min={0}
                // max={1}
                max={0.5}
                step={0.01}
                value={customRisk ?? undefined}
                name="userConfig.customRisk"
                onChange={handleInputEvent}
                disabled={risk !== "custom"}
                required={risk === "custom"}
                list="risks"
                className="mt--3"
              />
              <datalist id="risks" className="d-flex justify-content-between w-100 mt--4 text-uppercase small">
                <option value={0} label={tRisks("low")} className="text-primary" />
                <option value={0.5} label={tRisks("medium")} className="text-default" />
                {/*<option value={1} label="alto" className="text-danger"/>*/}
              </datalist>
            </Collapse>
            <FormDecoration.Line />

            {/*{sailingModeThreshold !== undefined &&*/}
            {/*  <FormInput.Switch*/}
            {/*    onChange={e => handleInputChange('userConfig.sailingModeThreshold', sailingModeThreshold === 40 ? 20 : 40)}*/}
            {/*    checked={sailingModeThreshold === 40}*/}
            {/*    label={<>*/}
            {/*      {t("form.sailingMode")}*/}
            {/*      /!*<span onClick={() => setInfoModal("MAB")} className="p-1 my--1 pointer-cursor">*!/*/}
            {/*      /!*  <i className="fas fa-info-circle"/>*!/*/}
            {/*      /!*</span>*!/*/}
            {/*    </>}*/}
            {/*    labels={["40%", "20%"]}*/}
            {/*    disabled={isLoading}*/}
            {/*  />*/}
            {/*}*/}

            {maxActiveBalanceOn !== undefined && (
              <>
                <FormInput.Switch
                  onChange={() => handleInputChange("userConfig.maxActiveBalance", maxActiveBalanceOn ? null : "")}
                  checked={maxActiveBalanceOn}
                  label={
                    <>
                      {t("form.maxActiveBalance")}
                      <span onClick={() => setInfoModal("MAB")} className="p-1 my--1 pointer-cursor mr-2">
                        <i className="fas fa-info-circle" />
                      </span>
                      <ProOnlyBadge />
                    </>
                  }
                  labels={t("form.maxActiveBalanceOptions", { returnObjects: true })}
                  disabled={isLoading}
                />
                <Collapse isOpen={maxActiveBalance != null}>
                  <FormInput.Input
                    type="number"
                    min={5000}
                    max={100000000}
                    value={maxActiveBalance ?? ""}
                    name="userConfig.maxActiveBalance"
                    onChange={handleInputEvent}
                    disabled={!maxActiveBalanceOn}
                    required={maxActiveBalanceOn}
                    className="mt--3"
                  />
                </Collapse>
              </>
            )}

            <FormGroup row className="d-flex align-items-center justify-content-between no-gutters mb-1">
              <Label className="col-auto form-control-label my-0 mr-2 d-flex justify-content-between w-100">
                <div>
                  {t("form.stoploss")}
                  <span onClick={() => setInfoModal("SL")} className="p-1 my--1 pointer-cursor">
                    <i className="fas fa-info-circle" />
                  </span>
                </div>
                <span>({stoplossLabel ? `${stoplossLabel}%` : " --- "})</span>
              </Label>
            </FormGroup>
            <MultiToggle
              options={[
                {
                  displayName: t("form.stoplossOnLabel"),
                  value: true,
                  optionClass: "bg-primary",
                },
                {
                  displayName: (
                    <div className="flex-center">
                      <span className="mr-1">
                        {t("form.stoplossDefaultLabel")}
                      </span>
                      <ProOnlyBadge compact />
                    </div>
                  ),
                  value: "custom",
                  optionClass: "bg-default",
                },
                {
                  displayName: t("form.stoplossOffLabel"),
                  value: false,
                },
              ]}
              // selectedOption={(!stopLossEnabled && stopLossEnabled !== false) ? true : useCustomStopLoss ? "custom" : stopLossEnabled}
              selectedOption={selectedStopLoss}
              onSelectOption={(v) => {
                const vNorm = typeof v === "number" ? String(v) : v;
                if (
                  selectedStopLoss !== false &&
                  selectedStopLoss !== "custom" &&
                  (vNorm === "custom" || vNorm === false)
                )
                  setStoplossWarning([vNorm, true]);
                else
                  handleInputChange("userConfig.advancedStopLoss", {
                    stopLossEnabled: !!v,
                    useCustomStopLoss: v === "custom",
                    customStopLoss: defaultCustomSL,
                  });
              }}
            />
            {strategy?.name === 'V6FX' && <p className="small text-muted mt-2">{t('form.v6StopOverlay')}</p>}
            <StoplossWarningModal
              isOpen={stoplossWarningModal}
              close={() => setStoplossWarning([null, false])}
              accept={() => {
                handleInputChange("userConfig.advancedStopLoss", {
                  stopLossEnabled: !!stoplossToAccept,
                  useCustomStopLoss: stoplossToAccept === "custom",
                  customStopLoss: defaultCustomSL,
                });
                setStoplossWarning([null, false]);
              }}
            />
            <Collapse isOpen={!!stopLossEnabled}>
              <FormInput.Range
                min={minSL}
                max={0.99}
                step={0.01}
                value={useCustomStopLoss ? (customStopLoss ?? undefined) : (defaultRiskStoploss ?? undefined)}
                onChange={(e) => {
                  const val = parseFloat(e.target?.value);
                  if (!useCustomStopLoss) {
                    handleInputChange("userConfig.advancedStopLoss", {
                      stopLossEnabled: true,
                      useCustomStopLoss: true,
                      customStopLoss: val,
                    });
                  } else {
                    handleInputChange("userConfig.advancedStopLoss.customStopLoss", val);
                  }
                }}
                disabled={!stopLossEnabled}
                required={!!useCustomStopLoss}
                list="customStoploss"
              />
              <datalist id="customStoploss" className="d-flex justify-content-between w-100 mt--4 small w-">
                <option value={minSL} label={`${minSL * 100}%`} />
                <option value={0.99} label="99%" />
              </datalist>
              {useCustomStopLoss &&
                defaultRiskStoploss !== null &&
                customStopLoss != null &&
                customStopLoss < defaultRiskStoploss &&
                !forcedCustomSlIsApplied && (
                  <InvalidFeedback>
                    <Trans
                      t={t}
                      i18nKey="form.customStoplossWarning"
                      components={{ b: <b /> }}
                      values={[(defaultRiskStoploss ?? 0) * 100]}
                    />
                  </InvalidFeedback>
                )}
              {showMediumRiskLowStoploss && (
                <small className="text-warning d-block mt-2">
                  <i className="fas fa-exclamation-triangle mr-1" />
                  {t("form.mediumRiskLowStoplossWarning")}
                </small>
              )}
            </Collapse>

            {!isVipOrZeroPriceTier(user) && !bot?.id && (
              <>
                <FormDecoration.Line />
                <PaymentMethodSelect
                  paymentMethod={paymentMethod}
                  setPaymentMethod={setPaymentMethod}
                  isDisabled={isLoading}
                  required
                />
              </>
            )}

            <div className="px-4">
              <FormInput.Input
                type="checkbox"
                checked={disclaimerAccepted}
                onChange={(e) => setDisclaimer([e.target.checked, false])}
                label={
                  <span style={{ marginBottom: "-2.5rem" }}>
                    <Trans
                      t={t}
                      i18nKey="disclaimerCheckbox"
                      components={{
                        disclaimer: (
                          <span
                            className="text-primary"
                            style={{ cursor: "pointer" }}
                            onClick={() => setDisclaimer([true, disclaimerAccepted])}
                          />
                        ),
                      }}
                    />
                  </span>
                }
                disabled={isLoading}
                required
              />
              <DisclaimerModal
                isOpen={disclaimerModal}
                close={() => setDisclaimer([false, disclaimerAccepted])}
                accept={() => setDisclaimer([false, true])}
              />
            </div>
          </>
        ) : (
          // The form only supports tradfi accounts today. Say so rather than
          // showing a modal with a title, a Create button and an empty body.
          <div className="px-4 py-5 text-center text-muted" role="status">
            <i className="fas fa-info-circle d-block mb-2" style={{ fontSize: "1.5rem" }} aria-hidden="true" />
            {t("unsupportedExchangeType")}
          </div>
        )}
        {/*</>}*/}
      </Modal.FormContainer>
    </>
  );
};

const RiskInfoModal = ({ isOpen, close }: { isOpen: boolean; close: () => void }) => {
  const { t } = useTranslation("bots", { keyPrefix: "createBotModal.riskInfoModal" });
  return (
    <Modal.Info titleInfo={t("title")} isOpen={isOpen} toggleOpen={close}>
      <Trans t={t} i18nKey="text" />
    </Modal.Info>
  );
};

const StoplossInfoModal = ({ isOpen, close }: { isOpen: boolean; close: () => void }) => {
  const { t } = useTranslation("bots", { keyPrefix: "createBotModal.stoplossInfoModal" });
  return (
    <Modal.Info titleInfo={t("title")} isOpen={isOpen} toggleOpen={close}>
      <Trans t={t} i18nKey="text" components={{ ul: <ul />, li: <li /> }} />
    </Modal.Info>
  );
};

const MABInfoModal = ({ isOpen, close }: { isOpen: boolean; close: () => void }) => {
  const { t } = useTranslation("bots", { keyPrefix: "createBotModal.MABInfoModal" });
  return (
    <Modal.Info titleInfo={t("title")} isOpen={isOpen} toggleOpen={close}>
      <Trans t={t} i18nKey="text" components={{ ul: <ul />, li: <li /> }} />
    </Modal.Info>
  );
};

const DisclaimerModal = ({ isOpen, close, accept }: { isOpen: boolean; close: () => void; accept: () => void }) => {
  const { t } = useTranslation("bots", { keyPrefix: "createBotModal.disclaimerModal" });
  return (
    <Modal.Simple
      title={t("title")}
      isOpen={isOpen}
      toggleOpen={close}
      closeText={t("closeBtn")}
      buttons={[
        {
          color: "success",
          children: t("acceptBtn"),
          onClick: accept,
          className: "w-auto",
        },
      ]}
    >
      <Trans t={t} i18nKey="text" />
    </Modal.Simple>
  );
};

const StoplossWarningModal = ({
  isOpen,
  close,
  accept,
}: {
  isOpen: boolean;
  close: () => void;
  accept: () => void;
}) => {
  // const [checked, setChecked] = useState(false)
  // useEffect(() => isOpen === false && setChecked(false), [isOpen])
  const { t } = useTranslation("bots", { keyPrefix: "createBotModal.stoplossWarningModal" });
  return (
    <Modal.Simple
      title={t("title")}
      isOpen={isOpen}
      toggleOpen={close}
      closeText={t("closeBtn")}
      buttons={[
        {
          color: "success",
          children: t("acceptBtn"),
          onClick: accept,
          className: "w-auto",
          // disabled: !checked
        },
      ]}
    >
      <Trans t={t} i18nKey="text" />
      {/*<div className="px-4">*/}
      {/*  <FormInput.Input*/}
      {/*    type="checkbox"*/}
      {/*    checked={checked}*/}
      {/*    onChange={e => setChecked(e.target.checked)}*/}
      {/*    label={<span style={{marginBottom: '-2.5rem'}}>{t("checkbox")}</span>}*/}
      {/*  />*/}
      {/*</div>*/}
    </Modal.Simple>
  );
};

export default UpdateBotModal;
